import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { InboxIcon } from 'lucide-react';

import type { UseFileAttachmentOptions } from './use-file-attachment';
import { useFileAttachment } from './use-file-attachment';

type FileAttachmentContextValue = {
  files: File[];
  openFilePicker: () => void;
  removeFile: (index: number) => void;
  disabled: boolean;
};

const FileAttachmentContext = createContext<FileAttachmentContextValue | null>(null);

export function useFileAttachmentContext(): FileAttachmentContextValue | null {
  return useContext(FileAttachmentContext);
}

export type WithFileUploadProps = {
  onFilesChange: (files: File[]) => void;
  onFileError?: (message: string) => void;
  allowedExtensions?: readonly string[];
  maxSizeBytes?: number;
  disabled?: boolean;
  className?: string;
  /** Controlled file list — see `useFileAttachment`. Omit to let this component keep the list. */
  files?: readonly File[];
  /** Controlled removal, paired with `files`. */
  onRemove?: (index: number) => void;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrop' | 'onDragEnter' | 'onDragLeave' | 'onDragOver' | 'onChange'>;

export function WithFileUpload({
  onFilesChange,
  onFileError,
  allowedExtensions,
  maxSizeBytes,
  disabled = false,
  className,
  files: controlledFiles,
  onRemove,
  children,
  ...rest
}: WithFileUploadProps) {
  const options: UseFileAttachmentOptions = {
    allowedExtensions,
    maxSizeBytes,
    onFilesChange,
    onFileError,
    disabled,
    files: controlledFiles,
    onRemove,
  };
  const { files, addFiles, removeFile, openPicker, inputRef } = useFileAttachment(options);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (disabled) return;
      dragCounter.current++;
      setIsDragging(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
      if (disabled) return;
      addFiles(Array.from(e.dataTransfer.files));
    },
    [disabled, addFiles],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (disabled) return;
      const files = Array.from(e.clipboardData.items)
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((f): f is File => !isNil(f));
      if (files.length === 0) return;
      e.preventDefault();
      addFiles(files);
    },
    [disabled, addFiles],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(e.target.files ?? []);
      if (selected.length > 0) addFiles(selected);
      e.target.value = '';
    },
    [addFiles],
  );

  return (
    <FileAttachmentContext.Provider value={{ files, openFilePicker: openPicker, removeFile, disabled }}>
      <div
        className={cn('relative', className)}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onPaste={handlePaste}
        {...rest}
      >
        {isDragging && (
          <div className="pointer-events-none absolute inset-0 z-50 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-primary bg-green-50/80">
            <InboxIcon size={32} className="mb-2 text-primary" />
            <p className="text-sm font-semibold text-primary">Drop files here</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          multiple
          onChange={handleInputChange}
          {...(allowedExtensions ? { accept: allowedExtensions.map((ext) => `.${ext}`).join(',') } : {})}
        />
        {children}
      </div>
    </FileAttachmentContext.Provider>
  );
}
